import React from 'react';
import { useHistory } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { useSignup } from '../context/SignupContext';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 700px;
  width: 100%;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #fa4616 0%, #ff6b3d 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 8px 24px rgba(250, 70, 22, 0.3);

  svg {
    width: 40px;
    height: 40px;
    color: #ffffff;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const BusinessName = styled.div`
  font-size: 18px;
  color: #fa4616;
  text-align: center;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #94a3b8;
  text-align: center;
  line-height: 1.6;
  margin: 0 0 32px 0;
`;

const ChecklistBox = styled.div`
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
`;

const ChecklistTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #22c55e;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 14px;
  color: ${(props) => (props.completed ? '#4ade80' : '#94a3b8')};

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const InfoBox = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;
`;

const InfoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoListItem = styled.li`
  font-size: 14px;
  color: #94a3b8;
  line-height: 1.8;
  padding-left: 28px;
  position: relative;
  margin-bottom: 10px;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4ade80;
    font-weight: 700;
    font-size: 16px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const PrimaryActionContainer = styled.div`
  margin-bottom: 20px;
`;

const ButtonHelper = styled.div`
  text-align: center;
  margin-top: 12px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;

  strong {
    color: #94a3b8;
    font-weight: 500;
  }
`;

const SecondaryActionContainer = styled.div`
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SkipLink = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 8px;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const SuccessStep = () => {
  const history = useHistory();
  const { formData } = useSignup();

  // Get platform name based on environment variable
  const getPlatformName = () => {
    const platform = process.env.REACT_APP_PLATFORM;
    switch (platform) {
      case 'pitpay':
        return 'Pit Pay';
      case 'tickethoss':
        return 'Ticket Hoss';
      case 'kartpass':
        return 'Kart Pass';
      default:
        return 'Pit Pay';
    }
  };

  const platformName = getPlatformName();

  const handleContinue = () => {
    history.push('/promoter-signup/stripe-setup');
  };

  const handleSkip = () => {
    // Navigate to login - they can log in and access their dashboard
    history.push('/login');
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <Card>
            <SuccessIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </SuccessIcon>

            <Title>Welcome to {platformName}!</Title>

            {formData.business_dba && (
              <BusinessName>{formData.business_dba}</BusinessName>
            )}

            <Subtitle>
              Your account is active and ready to go. Let's get you started!
            </Subtitle>

            <ChecklistBox>
              <ChecklistTitle>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                Quick Start Checklist
              </ChecklistTitle>

              <ChecklistItem completed>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Account created and activated
              </ChecklistItem>

              <ChecklistItem>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth="2" />
                </svg>
                Set up payment processing with Stripe
              </ChecklistItem>

              <ChecklistItem>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth="2" />
                </svg>
                Add team members to your account
              </ChecklistItem>

              <ChecklistItem>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth="2" />
                </svg>
                Add payment method for fees & credits
              </ChecklistItem>

              <ChecklistItem>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth="2" />
                </svg>
                Upload your event schedule
              </ChecklistItem>
            </ChecklistBox>

            <InfoBox>
              <InfoTitle>Here's What You Can Do</InfoTitle>
              <InfoList>
                <InfoListItem>
                  Access your dashboard and explore all features
                </InfoListItem>
                <InfoListItem>
                  Invite staff members to help manage your account
                </InfoListItem>
                <InfoListItem>
                  Set up a payment method for refund fees and message credits
                </InfoListItem>
                <InfoListItem>
                  Our team will reach out to help create your events
                </InfoListItem>
                <InfoListItem>
                  Connect with your dedicated account manager
                </InfoListItem>
              </InfoList>
            </InfoBox>

            <PrimaryActionContainer>
              <ButtonV2 onClick={handleContinue}>
                Set Up Payments with Stripe →
              </ButtonV2>
              <ButtonHelper>
                <strong>Recommended:</strong> Complete payment setup now to
                accept transactions and receive payouts. Takes about 5 minutes.
              </ButtonHelper>
            </PrimaryActionContainer>

            <SecondaryActionContainer>
              <SkipLink onClick={handleSkip}>
                I'll do this later, take me to my dashboard →
              </SkipLink>
            </SecondaryActionContainer>
          </Card>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default SuccessStep;
